import React, { useMemo, useRef, useState } from 'react';
import { generateWordSearchGrid } from '../utils/wordSearch';
import './WordSearchGrid.css';

function cellKey(r, c) {
  return `${r}-${c}`;
}

function normalizeDir(dr, dc) {
  return [Math.sign(dr), Math.sign(dc)];
}

export default function WordSearchGrid({ words, gridsize, onWordFound }) {
  const grid = useMemo(() => generateWordSearchGrid(words, gridsize), [words, gridsize]);
  const [path, setPath] = useState([]);
  const [foundCells, setFoundCells] = useState(new Set());
  const [foundWords, setFoundWords] = useState(new Set());
  const selectingRef = useRef(false);
  const startRef = useRef(null);

  const cellFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    if (!el || !el.dataset || el.dataset.row === undefined) return null;
    return { r: Number(el.dataset.row), c: Number(el.dataset.col) };
  };

  const buildPath = (start, end) => {
    const dr = end.r - start.r;
    const dc = end.c - start.c;
    if (dr === 0 && dc === 0) return [start];
    const steps = Math.max(Math.abs(dr), Math.abs(dc));
    const [ndr, ndc] = normalizeDir(dr, dc);
    // only allow straight lines (horizontal, vertical, diagonal)
    if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return [start];
    const cells = [];
    for (let i = 0; i <= steps; i++) {
      cells.push({ r: start.r + ndr * i, c: start.c + ndc * i });
    }
    return cells;
  };

  const checkMatch = (cells) => {
    const forward = cells.map((cell) => grid[cell.r][cell.c]).join('');
    const backward = forward.split('').reverse().join('');
    return words.find((w) => (w === forward || w === backward) && !foundWords.has(w));
  };

  const handleDown = (r, c) => {
    selectingRef.current = true;
    startRef.current = { r, c };
    setPath([{ r, c }]);
  };

  const handleMove = (clientX, clientY) => {
    if (!selectingRef.current) return;
    const cell = cellFromPoint(clientX, clientY);
    if (!cell) return;
    setPath(buildPath(startRef.current, cell));
  };

  const handleUp = () => {
    if (!selectingRef.current) return;
    selectingRef.current = false;
    setPath((currentPath) => {
      const match = checkMatch(currentPath);
      if (match) {
        setFoundWords((prev) => new Set(prev).add(match));
        setFoundCells((prev) => {
          const next = new Set(prev);
          currentPath.forEach((cell) => next.add(cellKey(cell.r, cell.c)));
          return next;
        });
        onWordFound && onWordFound(match);
      }
      return [];
    });
  };

  const pathKeys = useMemo(() => new Set(path.map((cell) => cellKey(cell.r, cell.c))), [path]);

  return (
    <div
      className="wordsearch-grid"
      style={{ gridTemplateColumns: `repeat(${gridsize}, 1fr)` }}
      onMouseUp={handleUp}
      onMouseLeave={handleUp}
      onTouchEnd={handleUp}
      onMouseMove={(e) => handleMove(e.clientX, e.clientY)}
      onTouchMove={(e) => {
        const touch = e.touches[0];
        if (touch) handleMove(touch.clientX, touch.clientY);
      }}
    >
      {grid.map((row, r) =>
        row.map((letter, c) => {
          const key = cellKey(r, c);
          const isFound = foundCells.has(key);
          const isSelecting = pathKeys.has(key);
          return (
            <div
              key={key}
              data-row={r}
              data-col={c}
              className={`wordsearch-cell ${isFound ? 'found' : ''} ${isSelecting ? 'selecting' : ''}`}
              onMouseDown={() => handleDown(r, c)}
              onTouchStart={() => handleDown(r, c)}
            >
              {letter}
            </div>
          );
        })
      )}
    </div>
  );
}
